import { Button } from '@atoms'
import { DropdownMenuItem, DropdownMenuSeparator } from '@atoms/DropdownMenu'
import { ModalLink } from '@inertiaui/modal-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@atoms/Tooltip'
import { DotsMenu } from '@components/molecules/DotsMenu'
import { scopedTranslation } from '@frontend/utils/I18n'
import { SUPPORTER_TABS } from '../../components/SupporterTabs'
import { supporterProfileUrl } from '../../utils/routes'
import { useDialog } from '@components/DialogProvider'
import type { SupporterEdge } from './useSupporterTable'

const t = scopedTranslation('group_show.table.action_buttons')
const tDemoSite = scopedTranslation('shared.demo_site')

type SupporterTableActionsProps = {
  supporterId: string
  firstName: string | null
  lastName: string | null
  groupId: string
  membership: SupporterEdge['membership']
  canSendIndividualSms: boolean
  canMakePhoneCalls: boolean
  demoSite: boolean
}

export function SupporterTableActions({
  supporterId,
  firstName,
  lastName,
  groupId,
  membership,
  canSendIndividualSms,
  canMakePhoneCalls,
  demoSite,
}: SupporterTableActionsProps) {
  const { open: openDialog } = useDialog()
  const profileUrl = supporterProfileUrl(groupId, supporterId)
  const name = firstName ?? ''
  const dialogData = { id: supporterId, firstName, lastName, groupId }

  const sendingOff = demoSite ? tDemoSite('sending_disabled') : null
  const callsOff = demoSite ? tDemoSite('calls_disabled') : null

  const dialogActions = [
    { type: 'logContact', icon: 'pencil-02', translationKey: 'log_contact', disabled: false, demoReason: null },
    {
      type: 'sendEmail',
      icon: 'mail-01',
      translationKey: 'email',
      disabled: demoSite || !membership?.emailable,
      demoReason: sendingOff,
    },
    {
      type: 'sendSms',
      icon: 'message-chat-square',
      translationKey: 'sms',
      disabled: demoSite || !canSendIndividualSms || !membership?.textable,
      demoReason: sendingOff,
    },
    {
      type: 'initiateCall',
      icon: 'phone-call-01',
      translationKey: 'call',
      disabled: demoSite || !canMakePhoneCalls || !membership?.callable,
      demoReason: callsOff,
    },
  ] as const

  return (
    <div className="tw:flex tw:items-center tw:justify-end tw:gap-1">
      {dialogActions.map(({ type, icon, translationKey, disabled, demoReason }) => (
        <Tooltip key={translationKey}>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              rank="secondary"
              leadingIcon={icon}
              ariaLabel={t(`${translationKey}.aria`, { name })}
              disabled={disabled}
              onClick={() => openDialog({ type, dialogData })}
            />
          </TooltipTrigger>
          <TooltipContent>{demoReason ?? t(`${translationKey}.label`)}</TooltipContent>
        </Tooltip>
      ))}

      <DotsMenu ariaLabel={t('more_actions.aria', { name })}>
        <DropdownMenuItem asChild>
          <ModalLink href={profileUrl} navigate>
            {t('view_profile.label')}
          </ModalLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <ModalLink href={supporterProfileUrl(groupId, supporterId, SUPPORTER_TABS.contacts)} navigate>
            {t('view_contacts.label')}
          </ModalLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <ModalLink href={supporterProfileUrl(groupId, supporterId, SUPPORTER_TABS.notes)} navigate>
            {t('view_notes.label')}
          </ModalLink>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {dialogActions
          .filter(({ disabled }) => !disabled)
          .map(({ type, translationKey }) => (
            <DropdownMenuItem key={translationKey} onClick={() => openDialog({ type, dialogData })}>
              {t(`${translationKey}.label`)}
            </DropdownMenuItem>
          ))}
      </DotsMenu>
    </div>
  )
}
